import React, { useState } from 'react';
import { Card, Badge, Button, Modal, Alert, Spinner } from 'react-bootstrap';
import { useCustomerToggle } from '../../hooks/useCustomerToggle';
import { autoDetectMapping } from '../../services/api/provisioningApi';

/**
 * CustomerToggle - Enable/disable toggle for provisioned customers.
 * Displays on the customer detail page for users with manage permissions.
 * Toggle ON requires validation checks to pass first.
 */
const CustomerToggle = ({ customerId }) => {
  const {
    status,
    loading,
    toggling,
    error,
    isEnabled,
    isDisabled,
    isNotProvisioned,
    isExternallyProvisioned,
    isUnknown,
    enable,
    disable,
    fetchStatus,
    clearError
  } = useCustomerToggle(customerId);

  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'enable' or 'disable'
  const [toggleError, setToggleError] = useState(null);
  const [autoDetecting, setAutoDetecting] = useState(false);

  const handleToggleClick = (action) => {
    setConfirmAction(action);
    setShowConfirm(true);
    setToggleError(null);
  };

  const handleConfirm = async () => {
    setShowConfirm(false);
    try {
      if (confirmAction === 'enable') {
        await enable();
      } else {
        await disable();
      }
    } catch (err) {
      setToggleError(err.message);
    }
  };

  const getStatusBadge = () => {
    if (loading) return <Badge bg="secondary">Loading...</Badge>;
    if (isEnabled) return <Badge bg="success">Enabled</Badge>;
    if (isDisabled) return <Badge bg="warning">Disabled</Badge>;
    if (isExternallyProvisioned) return <Badge bg="info">Externally Provisioned</Badge>;
    if (isNotProvisioned) return <Badge bg="secondary">Not Provisioned</Badge>;
    if (isUnknown) return <Badge bg="danger">Unknown</Badge>;
    return <Badge bg="secondary">—</Badge>;
  };

  const handleAutoDetect = async () => {
    try {
      setAutoDetecting(true);
      setToggleError(null);
      await autoDetectMapping(customerId);
      await fetchStatus();
    } catch (err) {
      setToggleError(err.response?.data?.error || 'Auto-detect failed');
    } finally {
      setAutoDetecting(false);
    }
  };

  if (loading) {
    return (
      <Card className="mb-3">
        <Card.Body className="d-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" />
          <span>Loading toggle status...</span>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <Card.Title className="mb-1 h6">IRIS Processing Toggle</Card.Title>
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted">Status:</span>
              {getStatusBadge()}
              {status?.ipsnetJobId && (
                <small className="text-muted">(ipsnet Job ID: {status.ipsnetJobId})</small>
              )}
            </div>
          </div>

          <div className="d-flex gap-2">
            {isDisabled && (
              <Button
                variant="success"
                size="sm"
                onClick={() => handleToggleClick('enable')}
                disabled={toggling}
              >
                {toggling ? <Spinner size="sm" /> : 'Enable'}
              </Button>
            )}
            {isEnabled && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleToggleClick('disable')}
                disabled={toggling}
              >
                {toggling ? <Spinner size="sm" /> : 'Disable'}
              </Button>
            )}
          </div>
        </div>

        {isNotProvisioned && (
          <Alert variant="info" className="mt-2 mb-0 py-2">
            <small>External mapping not configured — use the Provisioning Wizard to link external systems.</small>
          </Alert>
        )}

        {isExternallyProvisioned && (
          <Alert variant="warning" className="mt-2 mb-0 py-2">
            <small>
              External mapping not configured, but InvoiceDataCapture records were found for this customer.
              <Button variant="link" size="sm" className="p-0 ms-1" onClick={handleAutoDetect} disabled={autoDetecting}>
                {autoDetecting ? <Spinner size="sm" /> : 'Auto-detect'}
              </Button>
            </small>
          </Alert>
        )}

        {(isEnabled || isDisabled) && (
          <Alert variant="light" className="mt-2 mb-0 py-2 border-0">
            <small className="text-muted">
              This toggle controls document export from the scanning system (ipsnet only). BFE (is_rossum_enabled) and InvoiceDataCapture (is_iris_enabled) flags are set during initial provisioning and remain on permanently — they are not affected by this toggle.
            </small>
          </Alert>
        )}

        {(error || toggleError) && (
          <Alert variant="danger" className="mt-2 mb-0 py-2" dismissible onClose={() => { clearError(); setToggleError(null); }}>
            <small>{error || toggleError}</small>
          </Alert>
        )}
      </Card.Body>

      {/* Confirmation Modal */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {confirmAction === 'enable' ? 'Enable Customer' : 'Disable Customer'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmAction === 'enable' ? (
            <p>This will enable IRIS processing for this customer. Validation checks will run first to ensure the customer is properly configured.</p>
          ) : (
            <p>This will disable IRIS processing for this customer. Invoices will no longer be processed until re-enabled.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>Cancel</Button>
          <Button
            variant={confirmAction === 'enable' ? 'success' : 'danger'}
            onClick={handleConfirm}
          >
            {confirmAction === 'enable' ? 'Enable' : 'Disable'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default CustomerToggle;
